import { useEffect, useMemo, useRef, useState } from 'react'
import { isParamVisible, orderedParamSlots, paramSections, type ParamSlot } from './paramFormLayout'
import { createPortal } from 'react-dom'
import { usePanelPosition } from '../hooks/usePanelPosition'
import { useDraggablePanel } from '../hooks/useDraggablePanel'
import { usePortalDismiss } from '../hooks/usePortalDismiss'
import {
  getIndicatorDef,
  plotTypeOf,
  type FigureDeclaration,
  type FigureStyleOverride,
  type HistogramColors,
  type IndicatorDefinition,
  type IndicatorStyleOverrides,
  type ParamSchema,
} from '@edge/charts-indicators'
import {
  TIMEFRAME_CATEGORY_GROUPS,
  ALL_PERIOD_KEYS,
  isTimeframeVisibilityUnrestricted,
  type TimeframeVisibilityRule,
} from '../utils/timeframeVisibility'
import type { PeriodKey } from '../utils/resolution'
import {
  SECONDARY_BUTTON_CLS,
  DrawerSelect,
  ColorRow,
  DrawerNumberInput,
  INPUT_BOX_WIDE_CLS,
  PickerSwatch,
  LineWidthRow,
  LineStyleRow,
  PlotTypeRow,
  DrawerHeader,
  SettingRow,
  Toggle,
} from './drawerControls'
import {
  primaryButtonCls,
  drawerTabCls,
  composeColorOpacity,
  decomposeColorOpacity,
} from './drawerControls-utils'
import { normalizeHex } from '../utils/color'
import { useChartColorPicker } from './color-picker-context'
import { MTF_TIMEFRAMES, mtfTimeframeMs } from '../utils/mtfCalc'
import { Z_MODAL } from './zLayers'

/* ------------------------------------------------------------------ */
/*  Public API                                                          */
/* ------------------------------------------------------------------ */

/** The full editable payload the drawer emits on change / apply / save. */
export interface IndicatorSettingsPayload {
  calcParams?: unknown[]
  styles?: IndicatorStyleOverrides
  /** Per-timeframe visibility rule (undefined ⇒ visible on all timeframes). */
  timeframeVisibility?: TimeframeVisibilityRule
  /** Higher timeframe the indicator computes from (undefined ⇒ chart TF). */
  sourceTimeframe?: string
}

export interface IndicatorSettingsDrawerProps {
  /** klinecharts indicator name (e.g. 'MA', 'MACD') */
  indicatorName: string
  /**
   * Definition to use instead of the catalog lookup — for indicators outside
   * the catalog (e.g. backtest debug series). Typically synthesized with
   * empty `params` so only the Style section renders.
   */
  defOverride?: IndicatorDefinition
  /** User-facing label shown in the drawer header */
  headerLabel: string
  /** Current calcParams for this indicator */
  calcParams: unknown[] | undefined
  /** Current per-figure style overrides */
  styles: IndicatorStyleOverrides | undefined
  /** Current per-timeframe visibility rule, if any. */
  timeframeVisibility?: TimeframeVisibilityRule
  /** Current MTF source timeframe, if any (undefined ⇒ chart timeframe). */
  sourceTimeframe?: string
  /** Show the Timeframe select — MTF-eligible indicators only. */
  mtfEligible?: boolean
  /** The chart's own bar period, for disabling timeframes at or below it. */
  chartPeriodMs?: number
  /**
   * Rows a 1D/1W timeframe computes over on this chart (sessions or weeks), or
   * undefined while the daily series loads (`ChartManager.getMtfSourceBarCount`).
   * Fewer than the indicator's period draws no line, and the Timeframe row
   * names the shortfall.
   */
  mtfSourceBars?: (timeframe: string) => number | undefined
  /**
   * Hide the Visibility tab (e.g. external backtest series that don't switch
   * timeframes). Defaults to shown.
   */
  hideVisibilityTab?: boolean
  /** Called on every change so updates render live */
  onChange: (next: IndicatorSettingsPayload) => void
  /** Called when the user confirms — caller may persist defaults / broadcast */
  onApply?: (next: IndicatorSettingsPayload) => void
  /** Called when the user cancels — caller restores the pre-edit snapshot */
  onCancel: (snapshot: IndicatorSettingsPayload) => void
  /** Close the drawer */
  onClose: () => void
  /** Save current settings as the user's default for this indicator */
  onSaveAsDefault?: (next: IndicatorSettingsPayload) => void
  /** Apply current settings to all sibling charts (multi-chart panel) */
  onApplyToAllCharts?: (next: IndicatorSettingsPayload) => void
  /** Element the panel centers itself over (the chart area). Falls back to
   *  viewport-centering when absent; always clamped to the viewport, so a
   *  chart cell smaller than the panel never pushes it off screen. The panel
   *  always centers — it never anchors beside the opening click (the legend
   *  gear sits at the chart's top-left, and beside-click placement pinned a
   *  tall drawer to the app's top-left corner). Draggable by its header once
   *  open, via `useDraggablePanel`. */
  centerIn?: { current: HTMLElement | null }
}

// Inputs, Style, and Visibility are SEPARATE tabs. Complex indicators carry
// both many params and many style rows, so stacking Inputs+Style in one scroll
// is cramped — each gets its own tab. Visibility is a tall timeframe grid kept
// apart so its chips don't bleed into the others.
type DrawerTab = 'inputs' | 'style' | 'visibility'

const TAB_LABELS: Record<DrawerTab, string> = {
  inputs: 'Inputs',
  style: 'Style',
  visibility: 'Visibility',
}

/* ------------------------------------------------------------------ */
/*  Component                                                           */
/* ------------------------------------------------------------------ */

export function IndicatorSettingsDrawer({
  indicatorName,
  defOverride,
  headerLabel,
  calcParams,
  styles,
  timeframeVisibility,
  sourceTimeframe,
  mtfEligible,
  chartPeriodMs,
  mtfSourceBars,
  hideVisibilityTab,
  onChange,
  onApply,
  onCancel,
  onClose,
  onSaveAsDefault,
  onApplyToAllCharts,
  centerIn,
}: IndicatorSettingsDrawerProps) {
  const def = defOverride ?? getIndicatorDef(indicatorName)

  // Pre-edit snapshot — cloned once on mount so Cancel reverts cleanly.
  const [snapshot] = useState<IndicatorSettingsPayload>(() => ({
    calcParams: calcParams ? [...calcParams] : undefined,
    styles: styles ? cloneStyles(styles) : undefined,
    timeframeVisibility: timeframeVisibility ? cloneRule(timeframeVisibility) : undefined,
    sourceTimeframe,
  }))

  // Working copies for the form; all flow back through onChange live.
  const [workingParams, setWorkingParams] = useState<unknown[]>(() =>
    calcParams ? [...calcParams] : [],
  )
  const [workingStyles, setWorkingStyles] = useState<IndicatorStyleOverrides>(() =>
    styles ? cloneStyles(styles) : {},
  )
  // The Visibility tab edits which timeframes the indicator shows on. An empty
  // rule = visible everywhere; a period set to false hides it there. It's only
  // emitted back (persisted) once the user hides at least one timeframe.
  const [workingRule, setWorkingRule] = useState<TimeframeVisibilityRule>(() =>
    timeframeVisibility ? cloneRule(timeframeVisibility) : {},
  )
  const [workingSourceTf, setWorkingSourceTf] = useState<string | undefined>(sourceTimeframe)

  // Inputs shows when the def has params; Style when it has figures to style;
  // Visibility unless suppressed.
  const availableTabs = useMemo<DrawerTab[]>(() => {
    const tabs: DrawerTab[] = []
    if (def && def.params.length > 0) tabs.push('inputs')
    if (def && def.figures.length > 0) tabs.push('style')
    if (!hideVisibilityTab) tabs.push('visibility')
    return tabs
  }, [def, hideVisibilityTab])

  const [activeTab, setActiveTab] = useState<DrawerTab>(() =>
    def && def.params.length > 0
      ? 'inputs'
      : def && def.figures.length > 0
        ? 'style'
        : 'visibility',
  )

  // Guard: if the active tab is removed (e.g. defOverride changes), switch to first available.
  useEffect(() => {
    if (availableTabs.length > 0 && !availableTabs.includes(activeTab)) {
      setActiveTab(availableTabs[0])
    }
  }, [availableTabs, activeTab])

  // Ref for the panel — used by the click-outside handler.
  const drawerRef = useRef<HTMLDivElement>(null)

  // Centered over the chart — two-pass measured. Header-drag overrides this
  // once the user drags the panel (see `useDraggablePanel`).
  const centerPos = usePanelPosition(drawerRef, { centerIn })
  const { pos, onHeaderPointerDown } = useDraggablePanel(drawerRef, centerPos)

  // Esc + outside-click dismiss = KEEP the live edits + close (matching the
  // chart settings modal — edits apply live and persist on dismiss). Only the
  // explicit footer Cancel button reverts to the pre-edit snapshot. The gear's
  // own onClick toggle is guarded so a second click doesn't bounce-reopen; the
  // host color picker's Radix portal is guarded inside the hook.
  usePortalDismiss(drawerRef, onClose, { guardSelector: '[data-action="settings"]' })

  // Compose the emit payload from the current working values. A rule that hides
  // nothing (every timeframe visible) is emitted as `undefined` so we never
  // persist a no-op restriction.
  const buildPayload = (
    params: unknown[],
    styleOv: IndicatorStyleOverrides,
    rule: TimeframeVisibilityRule,
    sourceTf: string | undefined,
  ): IndicatorSettingsPayload => ({
    calcParams: params.length > 0 ? params : undefined,
    styles: Object.keys(styleOv).length > 0 ? styleOv : undefined,
    timeframeVisibility: isTimeframeVisibilityUnrestricted(rule) ? undefined : cloneRule(rule),
    sourceTimeframe: sourceTf,
  })

  const emit = (
    params: unknown[],
    styleOv: IndicatorStyleOverrides,
    rule: TimeframeVisibilityRule,
    sourceTf: string | undefined,
  ) => {
    onChange(buildPayload(params, styleOv, rule, sourceTf))
  }

  const handleParamChange = (index: number, value: unknown) => {
    // Emit the FULL positional array. A study added without explicit
    // calcParams (MACD, KDJ, DMI...) opens with none, and klinecharts
    // replaces calcParams wholesale: editing only Fast would send `[15]`,
    // leave Slow/Signal undefined, and NaN the whole calc (the pane blanks).
    // Holes (and nulls, which is what a hole becomes once saved as JSON) take
    // the schema default, which is what the inputs already show.
    const next = [...workingParams]
    def?.params.forEach((schema, i) => {
      if (next[i] == null) next[i] = schema.default
    })
    next[index] = value
    setWorkingParams(next)
    emit(next, workingStyles, workingRule, workingSourceTf)
  }

  const handleFigureStyleChange = (figureKey: string, patch: Partial<FigureStyleOverride>) => {
    const next: IndicatorStyleOverrides = {
      ...workingStyles,
      [figureKey]: { ...(workingStyles[figureKey] ?? {}), ...patch },
    }
    setWorkingStyles(next)
    emit(workingParams, next, workingRule, workingSourceTf)
  }

  // Several figures in ONE update — the paired layout writes width/style to
  // both up and down; two sequential single-key calls would drop the first.
  const handleFigureStylesChange = (entries: Array<[string, Partial<FigureStyleOverride>]>) => {
    const next: IndicatorStyleOverrides = { ...workingStyles }
    for (const [key, patch] of entries) {
      next[key] = { ...(next[key] ?? {}), ...patch }
    }
    setWorkingStyles(next)
    emit(workingParams, next, workingRule, workingSourceTf)
  }

  // Per-figure reset — drop just this figure's override so the catalog default
  // takes over again (mirrors the whole-indicator `handleReset`, scoped to one
  // key). Deleting the entry restores every element of the figure at once:
  // both up/down colours + opacity for a bar row, colour + width + style for a
  // line row.
  const handleFigureReset = (figureKey: string) => {
    const next: IndicatorStyleOverrides = { ...workingStyles }
    delete next[figureKey]
    setWorkingStyles(next)
    emit(workingParams, next, workingRule, workingSourceTf)
  }

  const handleRuleChange = (period: PeriodKey, visible: boolean) => {
    const next: TimeframeVisibilityRule = { ...workingRule, [period]: visible }
    setWorkingRule(next)
    emit(workingParams, workingStyles, next, workingSourceTf)
  }

  // "All on / All off": on ⇒ empty rule (unrestricted, persisted as undefined);
  // off ⇒ every period explicitly false.
  const handleRuleSetAll = (visible: boolean) => {
    const next: TimeframeVisibilityRule = visible
      ? {}
      : Object.fromEntries(ALL_PERIOD_KEYS.map((k) => [k, false]))
    setWorkingRule(next)
    emit(workingParams, workingStyles, next, workingSourceTf)
  }

  const handleSourceTfChange = (tf: string | undefined) => {
    setWorkingSourceTf(tf)
    emit(workingParams, workingStyles, workingRule, tf)
  }

  const handleReset = () => {
    const defaultParams = def ? def.params.map((p) => p.default) : []
    setWorkingParams(defaultParams)
    setWorkingStyles({})
    setWorkingRule({})
    setWorkingSourceTf(undefined)
    emit(defaultParams, {}, {}, undefined)
  }

  const handleCancel = () => {
    // The host treats an `undefined` calcParams as "no change", so an
    // indicator opened with none saved would keep every live param edit.
    // Revert it to explicit catalog defaults, only when edited. (Undefined
    // `styles` already means catalog defaults to the host; the explicit empty
    // set below is kept so the snapshot says so outright.) An untouched Cancel
    // still hands back the snapshot as-is.
    const snap = snapshot
    const revert: IndicatorSettingsPayload = { ...snap }
    if (snap.calcParams === undefined && workingParams.length > 0 && def) {
      revert.calcParams = def.params.map((p) => p.default)
    }
    if (snap.styles === undefined && Object.keys(workingStyles).length > 0) {
      revert.styles = {}
    }
    onCancel(revert)
    onClose()
  }

  const handleSaveDefault = () => {
    onSaveAsDefault?.(buildPayload(workingParams, workingStyles, workingRule, workingSourceTf))
  }

  const handleApply = () => {
    onApply?.(buildPayload(workingParams, workingStyles, workingRule, workingSourceTf))
    onClose()
  }

  const snap = snapshot
  const isDirty =
    JSON.stringify(snap.calcParams ?? []) !== JSON.stringify(workingParams) ||
    JSON.stringify(snap.styles ?? {}) !== JSON.stringify(workingStyles) ||
    JSON.stringify(snap.timeframeVisibility ?? null) !==
      JSON.stringify(isTimeframeVisibilityUnrestricted(workingRule) ? null : workingRule) ||
    (snap.sourceTimeframe ?? null) !== (workingSourceTf ?? null)

  const panel = (
    <div
      ref={drawerRef}
      data-chart-popup=""
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      // Fixed-position panel portaled to <body> — same chrome as SettingsModal.
      // Centered over the chart area (viewport-clamped); hidden until the
      // two-pass measurement above has placed it.
      className="fixed flex w-[360px] flex-col overflow-hidden rounded-xl border border-primary/15 bg-popover text-[hsl(var(--chart-text))] shadow-2xl shadow-black/60"
      style={{
        zIndex: Z_MODAL,
        maxHeight: 'min(720px, 90vh)',
        left: pos?.left ?? 0,
        top: pos?.top ?? 0,
        visibility: pos ? 'visible' : 'hidden',
      }}
    >
      {/* Header (sticky). The plain catalog name only — the param string
          (EMA(72) · 15m) already lives on the legend row the user clicked, and
          the description added a sentence nobody needed to re-read here. */}
      <DrawerHeader
        label={def?.label ?? headerLabel}
        // Header X = close and KEEP live edits (like the chart settings modal);
        // only the footer Cancel reverts to the pre-edit snapshot.
        onClose={onClose}
        closeAriaLabel="Close indicator settings"
        onPointerDown={onHeaderPointerDown}
      />

      {/* Tab bar — same underlined segmented control as SettingsModal */}
      {availableTabs.length > 0 && (
        <div className="flex shrink-0 border-b border-border-strong px-2">
          {availableTabs.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setActiveTab(t)}
              className={drawerTabCls(activeTab === t)}
            >
              {TAB_LABELS[t]}
            </button>
          ))}
        </div>
      )}

      {/* Content — every tab stays mounted, stacked in one grid cell; inactive
          tabs are invisible but still size the cell. The section therefore
          keeps the height of the tallest tab (Visibility) as its minimum, so
          switching tabs never resizes the panel. */}
      <div className="flex-1 overflow-y-auto px-3 py-3" style={{ scrollbarWidth: 'thin' }}>
        {!def && <UnknownIndicatorNotice name={indicatorName} />}
        <div className="grid">
          {def && def.params.length > 0 && (
            <div
              className={
                'col-start-1 row-start-1 ' +
                (activeTab === 'inputs' ? '' : 'invisible pointer-events-none')
              }
              aria-hidden={activeTab !== 'inputs'}
            >
              <InputsTab
                def={def}
                params={workingParams}
                chartPeriodMs={chartPeriodMs}
                onChange={handleParamChange}
              />
              {mtfEligible && (
                <SourceTimeframeRow
                  value={workingSourceTf}
                  chartPeriodMs={chartPeriodMs}
                  period={Number(workingParams[0])}
                  sourceBars={mtfSourceBars}
                  onChange={handleSourceTfChange}
                />
              )}
            </div>
          )}
          {def && def.figures.length > 0 && (
            <div
              className={
                'col-start-1 row-start-1 ' +
                (activeTab === 'style' ? '' : 'invisible pointer-events-none')
              }
              aria-hidden={activeTab !== 'style'}
            >
              <StyleTab
                def={def}
                styles={workingStyles}
                onChange={handleFigureStyleChange}
                onChangeMany={handleFigureStylesChange}
                onReset={handleFigureReset}
              />
            </div>
          )}
          {!hideVisibilityTab && (
            <div
              className={
                'col-start-1 row-start-1 ' +
                (activeTab === 'visibility' ? '' : 'invisible pointer-events-none')
              }
              aria-hidden={activeTab !== 'visibility'}
            >
              <VisibilityTab
                rule={workingRule}
                onToggle={handleRuleChange}
                onSetAll={handleRuleSetAll}
              />
            </div>
          )}
        </div>
      </div>

      <Footer
        isDirty={isDirty}
        onCancel={handleCancel}
        onApply={handleApply}
        onReset={handleReset}
        onSaveAsDefault={onSaveAsDefault ? handleSaveDefault : undefined}
        onApplyToAll={
          onApplyToAllCharts
            ? () =>
                onApplyToAllCharts(
                  buildPayload(workingParams, workingStyles, workingRule, workingSourceTf),
                )
            : undefined
        }
      />
    </div>
  )

  return createPortal(panel, document.body)
}

/* ------------------------------------------------------------------ */
/*  Inputs tab                                                          */
/* ------------------------------------------------------------------ */

function InputsTab({
  def,
  params,
  chartPeriodMs,
  onChange,
}: {
  def: IndicatorDefinition
  params: unknown[]
  chartPeriodMs?: number
  onChange: (index: number, value: unknown) => void
}) {
  if (def.params.length === 0) {
    return (
      <div className="py-3 text-xs text-[hsl(var(--muted-foreground))]">
        This indicator has no configurable inputs.
      </div>
    )
  }
  const control = ({ schema, index }: ParamSlot) => (
    <ParamControl
      key={schema.key}
      schema={schema}
      value={params[index] ?? schema.default}
      chartPeriodMs={chartPeriodMs}
      onChange={(v) => onChange(index, v)}
    />
  )
  // Display order and visibility both diverge from the array: `i` stays the positional
  // calcParams slot (old persisted arrays must not shift) while `order` moves the control
  // and `showWhen` drops one that cannot apply to the current selection.
  const slots = orderedParamSlots(def.params).filter(({ schema }) => isParamVisible(schema, params))
  if (!slots.some(({ schema }) => schema.group !== undefined || schema.inline !== undefined))
    return <div className="flex flex-col gap-3.5">{slots.map(control)}</div>
  // a script's Pine input layout: `group` sections under a header, `inline` rows
  return (
    <div className="flex flex-col gap-3.5">
      {paramSections(slots).map((section, i) => (
        <div key={section.group ?? `loose-${i}`} className="flex flex-col gap-3.5">
          {section.group !== undefined && (
            <div
              role="heading"
              aria-level={3}
              className="border-b border-border-strong pb-1 text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--muted-foreground))]"
            >
              {section.group}
            </div>
          )}
          {section.rows.map((row) =>
            row.length === 1 ? (
              control(row[0]!)
            ) : (
              <div
                key={row[0]!.schema.key}
                data-testid={`param-row-${row[0]!.schema.key}`}
                className="flex items-start gap-3 [&>*]:min-w-0 [&>*]:flex-1"
              >
                {row.map(control)}
              </div>
            ),
          )}
        </div>
      ))}
    </div>
  )
}

function ParamControl({
  schema,
  value,
  chartPeriodMs,
  onChange,
}: {
  schema: ParamSchema
  value: unknown
  chartPeriodMs?: number
  onChange: (v: unknown) => void
}) {
  // Timeframe params stay freely selectable; a pick that is not above the
  // chart's own period simply does nothing yet, and the cue says so.
  const tfMs = schema.timeframeParam ? mtfTimeframeMs(String(value)) : undefined
  const tfInactive = tfMs != null && chartPeriodMs != null && tfMs <= chartPeriodMs
  return (
    <div className="flex flex-col gap-1">
      <div className="flex min-h-6 items-center justify-between">
        <label className="flex items-center gap-1 text-xs font-medium text-[hsl(var(--muted-foreground))]">
          {schema.label}
          {schema.unit && <span className="ml-1.5 opacity-60">{schema.unit}</span>}
          {schema.description && <InfoTip text={schema.description} />}
        </label>
        {schema.type === 'int' || schema.type === 'float' ? (
          <DrawerNumberInput
            value={Number(value)}
            min={schema.min}
            max={schema.max}
            step={schema.step ?? (schema.type === 'int' ? 1 : 0.1)}
            integer={schema.type === 'int'}
            fallback={schema.default}
            onChange={onChange}
          />
        ) : schema.type === 'enum' ? (
          <EnumSelect schema={schema} value={String(value)} onChange={onChange} />
        ) : schema.type === 'text' ? (
          schema.multiline ? null : (
            <input
              type="text"
              aria-label={schema.label}
              value={String(value ?? '')}
              onChange={(e) => onChange(e.target.value)}
              className={INPUT_BOX_WIDE_CLS}
            />
          )
        ) : schema.type === 'color' ? (
          <PickerSwatch
            value={String(value ?? schema.default)}
            onChange={onChange}
            title={schema.label}
          />
        ) : (
          <Toggle checked={Boolean(value)} onChange={onChange} />
        )}
      </div>
      {schema.type === 'text' && schema.multiline && (
        <textarea
          aria-label={schema.label}
          value={String(value ?? '')}
          rows={3}
          onChange={(e) => onChange(e.target.value)}
          className="w-full resize-y rounded border border-border-strong bg-field px-2 py-1 text-xs text-[hsl(var(--chart-text))] outline-hidden focus:border-primary"
        />
      )}
      {tfInactive && (
        <div className="text-[10px] leading-snug text-amber-500">
          Must be higher than the chart timeframe — it will not show until the chart is below{' '}
          {String(value)}.
        </div>
      )}
    </div>
  )
}

/**
 * Instant CSS tooltip on a small info icon — the native `title` needs a second
 * of dead-still hover and browsers sometimes swallow it entirely inside
 * portaled overlays. Shown above the icon so scroll containers cannot clip it.
 */
function InfoTip({ text }: { text: string }) {
  return (
    <span className="group relative flex items-center">
      <svg
        width={11}
        height={11}
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
        className="opacity-60"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
      </svg>
      <span
        role="tooltip"
        className="pointer-events-none absolute bottom-full left-0 z-10 mb-1.5 hidden w-56 rounded border border-border-strong bg-popover p-2 text-[11px] font-normal leading-snug text-[hsl(var(--chart-text))] shadow-lg group-hover:block"
      >
        {text}
      </span>
    </span>
  )
}

/**
 * MTF source-timeframe select — which timeframe the indicator COMPUTES from.
 *
 * 'Chart' (undefined) follows the chart's own period. Every timeframe stays
 * selectable: the wrapped calc passes through whenever the selection is not
 * above the chart's own period, so the choice is kept and simply takes effect
 * once the user drops to a lower chart timeframe. An inline cue marks that
 * inactive state instead of disabling options. 1D and 1W compute from a
 * fetched daily series (MTF Phase B); when it holds fewer rows than the
 * indicator's period (a base plan keeps about six months) no line can draw,
 * and a second cue names the shortfall.
 */
function SourceTimeframeRow({
  value,
  chartPeriodMs,
  period,
  sourceBars,
  onChange,
}: {
  value: string | undefined
  chartPeriodMs: number | undefined
  period: number
  sourceBars: ((timeframe: string) => number | undefined) | undefined
  onChange: (tf: string | undefined) => void
}) {
  const selectedMs = mtfTimeframeMs(value)
  // Selected but not above the chart's period — the calc is passing through.
  const inactive = selectedMs != null && chartPeriodMs != null && selectedMs <= chartPeriodMs
  const available =
    !inactive && (value === '1D' || value === '1W') ? sourceBars?.(value) : undefined
  const shortfall = available != null && Number.isFinite(period) && available < period
  return (
    <div className="mt-3.5 flex flex-col gap-1">
      <div className="flex min-h-6 items-center justify-between">
        <label className="flex items-center gap-1 text-xs font-medium text-[hsl(var(--muted-foreground))]">
          Timeframe
          <InfoTip text="Compute from a higher timeframe; values hold flat across each bucket." />
        </label>
        <DrawerSelect
          ariaLabel="Source timeframe"
          value={value ?? ''}
          options={[
            { value: '', label: 'Chart' },
            ...MTF_TIMEFRAMES.map((tf) => ({ value: tf, label: tf })),
          ]}
          onChange={(v) => onChange(v === '' ? undefined : v)}
        />
      </div>
      {inactive && (
        <div className="text-[10px] leading-snug text-amber-500">
          Only shows on chart timeframes below {value}. Using chart values for now.
        </div>
      )}
      {shortfall && (
        <div className="text-[10px] leading-snug text-amber-500">
          Needs {period} {value === '1W' ? 'weekly' : 'daily'} bars, {available} available.
        </div>
      )}
    </div>
  )
}

function EnumSelect({
  schema,
  value,
  onChange,
}: {
  schema: Extract<ParamSchema, { type: 'enum' }>
  value: string
  onChange: (v: string) => void
}) {
  // The default w-24 box truncates longer option labels ("Prev day high");
  // widen the trigger when any option needs the room.
  const wide = schema.options.some((o) => o.label.length > 10)
  return (
    <DrawerSelect
      ariaLabel={schema.label}
      value={value}
      options={schema.options}
      onChange={onChange}
      buttonClassName={wide ? INPUT_BOX_WIDE_CLS : undefined}
    />
  )
}

/* ------------------------------------------------------------------ */
/*  Style tab                                                           */
/* ------------------------------------------------------------------ */

function StyleTab({
  def,
  styles,
  onChange,
  onChangeMany,
  onReset,
}: {
  def: IndicatorDefinition
  styles: IndicatorStyleOverrides
  onChange: (figureKey: string, patch: Partial<FigureStyleOverride>) => void
  onChangeMany: (entries: Array<[string, Partial<FigureStyleOverride>]>) => void
  onReset: (figureKey: string) => void
}) {
  // An up/down pair (HTF candles) is ONE visual element with two colours, not
  // two independent lines — so it gets one Colors row with both swatches and a
  // single width/style pair that writes to both figures, mirroring how the
  // chart settings treat candle bodies. Extra figures (Border) follow.
  const paired =
    def.figures.length >= 2 && def.figures[0].key === 'up' && def.figures[1].key === 'down'
  if (paired) {
    const [up, down, ...rest] = def.figures
    const upOv = styles[up.key] ?? {}
    const downOv = styles[down.key] ?? {}
    const both = (patch: Partial<FigureStyleOverride>) =>
      onChangeMany([
        [up.key, patch],
        [down.key, patch],
      ])
    // One flat list mirroring the chart settings' candle rows: Body, Border,
    // Wicks each as label + two swatches + reset + eye, then the shared
    // width/style.
    return (
      <div className="flex flex-col gap-2.5 py-3 pt-0">
        <StylePairRow
          label="Body"
          upFig={up}
          downFig={down}
          upOverride={upOv}
          downOverride={downOv}
          fallbackUp={up.defaultColor}
          fallbackDown={down.defaultColor}
          showOpacity
          onChange={onChange}
          onChangeMany={onChangeMany}
        />
        {chunkPairs(rest).map(([upFig, downFig]) => (
          <StylePairRow
            key={upFig.key}
            label={upFig.label}
            upFig={upFig}
            downFig={downFig}
            upOverride={styles[upFig.key] ?? {}}
            downOverride={downFig ? (styles[downFig.key] ?? {}) : {}}
            // Follow-the-body sentinel: unstyled swatches show the body's own
            // current colours, which is what actually draws.
            fallbackUp={upOv.color ?? up.defaultColor}
            fallbackDown={downOv.color ?? down.defaultColor}
            onChange={onChange}
            onChangeMany={onChangeMany}
          />
        ))}
        <LineWidthRow
          value={upOv.size ?? up.defaultSize ?? 1}
          onChange={(v) => both({ size: v })}
        />
        <LineStyleRow
          value={(upOv.style ?? up.defaultStyle ?? 'solid') as 'solid' | 'dashed' | 'dotted'}
          onChange={(v) => both({ style: v })}
        />
      </div>
    )
  }
  return (
    <div className="flex flex-col">
      {def.figures.map((figure, i) => (
        <FigureStyleRow
          key={figure.key}
          figure={figure}
          override={styles[figure.key] ?? {}}
          onChange={(patch) => onChange(figure.key, patch)}
          onReset={() => onReset(figure.key)}
          isLast={i === def.figures.length - 1}
          plotTypeFixed={!!def.fixedPlotTypes}
          // A lone figure's label just repeats the drawer title (EMA's only
          // line is labelled "EMA") — the header only earns its row when there
          // are several figures to tell apart, or a hide checkbox to host.
          showHeader={def.figures.length > 1 || !!figure.hideable}
        />
      ))}
    </div>
  )
}

/** Group trailing figures into up/down pairs, in declaration order. */
function chunkPairs(figures: FigureDeclaration[]): Array<[FigureDeclaration, FigureDeclaration?]> {
  const out: Array<[FigureDeclaration, FigureDeclaration?]> = []
  for (let i = 0; i < figures.length; i += 2) out.push([figures[i], figures[i + 1]])
  return out
}

/**
 * One styled element of a paired indicator (Body / Border / Wicks): label, the
 * up/down colour swatches, a follow-default reset (only while customized — the
 * FollowThemeReset idiom), and an eye that hides the element outright. The eye
 * replaces per-row checkboxes; visibility rides the pair's `up` figure.
 * Width/style are deliberately absent — every element follows the body's
 * shared Line width.
 */
function StylePairRow({
  label,
  upFig,
  downFig,
  upOverride,
  downOverride,
  fallbackUp,
  fallbackDown,
  showOpacity = false,
  onChange,
  onChangeMany,
}: {
  label: string
  upFig: FigureDeclaration
  downFig?: FigureDeclaration
  upOverride: FigureStyleOverride
  downOverride: FigureStyleOverride
  fallbackUp: string
  fallbackDown: string
  showOpacity?: boolean
  onChange: (figureKey: string, patch: Partial<FigureStyleOverride>) => void
  onChangeMany: (entries: Array<[string, Partial<FigureStyleOverride>]>) => void
}) {
  const Picker = useChartColorPicker()
  const shown = upOverride.visible !== false
  const customized =
    upOverride.color != null ||
    upOverride.opacity != null ||
    upOverride.visible === false ||
    downOverride.color != null ||
    downOverride.opacity != null
  const resetPatch: Partial<FigureStyleOverride> = {
    color: undefined,
    opacity: undefined,
    visible: undefined,
  }
  const swatch = (
    fig: FigureDeclaration,
    override: FigureStyleOverride,
    fallback: string,
    title: string,
  ) => {
    const color = override.color || fig.defaultColor || fallback
    const value = showOpacity
      ? composeColorOpacity(normalizeHex(color), override.opacity ?? 1)
      : normalizeHex(color)
    const apply = (next: string) => {
      const d = decomposeColorOpacity(next)
      onChange(fig.key, showOpacity ? { color: d.hex, opacity: d.alpha } : { color: d.hex })
    }
    return Picker ? (
      Picker({ value, onChange: apply, title, showOpacity })
    ) : (
      <input
        type="color"
        title={title}
        value={normalizeHex(color)}
        onChange={(e) => apply(e.target.value)}
        className="h-5 w-5 cursor-pointer rounded ring-1 ring-inset ring-[hsl(var(--chart-text)/0.2)] bg-transparent p-0"
      />
    )
  }
  return (
    <SettingRow label={label}>
      <div className="flex items-center gap-1.5">
        {customized && (
          <button
            type="button"
            onClick={() =>
              onChangeMany(
                downFig
                  ? [
                      [upFig.key, resetPatch],
                      [downFig.key, resetPatch],
                    ]
                  : [[upFig.key, resetPatch]],
              )
            }
            title={`Reset ${label.toLowerCase()} to default`}
            aria-label={`Reset ${label} to default`}
            className="flex h-5 w-5 items-center justify-center rounded-sm text-[hsl(var(--muted-foreground))] transition-colors hover:bg-accent hover:text-[hsl(var(--chart-text))]"
          >
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor">
              <path
                d="M3 12a9 9 0 1 0 3-6.7L3 8"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path d="M3 3v5h5" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        )}
        {swatch(upFig, upOverride, fallbackUp, `${label} (up)`)}
        {downFig && swatch(downFig, downOverride, fallbackDown, `${label} (down)`)}
        <button
          type="button"
          onClick={() => onChange(upFig.key, { visible: !shown })}
          title={shown ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
          aria-label={`${shown ? 'Hide' : 'Show'} ${label}`}
          aria-pressed={!shown}
          className={
            'flex h-5 w-5 items-center justify-center rounded-sm transition-colors hover:bg-accent ' +
            (shown
              ? 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--chart-text))]'
              : 'text-[hsl(var(--chart-text-muted))] opacity-60')
          }
        >
          {shown ? (
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor">
              <path
                d="M2 12s3.5-6 10-6 10 6 10 6-3.5 6-10 6-10-6-10-6Z"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <circle cx="12" cy="12" r="2.5" strokeWidth="2" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor">
              <path
                d="M2 12s3.5-6 10-6c1.9 0 3.6.5 5 1.2M22 12s-3.5 6-10 6c-1.9 0-3.6-.5-5-1.2"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path d="m4 20 16-16" strokeWidth="2" strokeLinecap="round" />
            </svg>
          )}
        </button>
      </div>
    </SettingRow>
  )
}

function FigureStyleRow({
  figure,
  override,
  onChange,
  onReset,
  isLast,
  showHeader,
  plotTypeFixed,
}: {
  figure: FigureDeclaration
  override: FigureStyleOverride
  onChange: (patch: Partial<FigureStyleOverride>) => void
  onReset: () => void
  isLast: boolean
  showHeader: boolean
  plotTypeFixed: boolean
}) {
  // Rows follow what the figure is DRAWN as: a bar drawn as a line takes one line
  // color plus width and dash; a line drawn as columns loses both.
  const plotType = plotTypeOf(figure, override)
  const asLine = plotType === 'line'
  const hasUpDown =
    !asLine && (figure.defaultUpColor || figure.defaultDownColor || override.upColor)
  const shown = override.visible !== false
  // Any stored key means this figure diverges from its catalog default — offer
  // a per-figure reset that drops the whole override (colours + opacity for a
  // bar, colour + width + style for a line). Nothing to reset when it's clean.
  const customized = Object.keys(override).length > 0
  return (
    <div
      // first:pt-0 / last:pb-0: the tab container already pads the block, so
      // the edge rows must not stack their own padding on top of it — that was
      // the Style tab starting visibly lower than Inputs and Visibility.
      className={
        'flex flex-col gap-2.5 py-3 first:pt-0 last:pb-0 ' +
        (isLast ? '' : 'border-b border-border-strong')
      }
    >
      {showHeader && (
        <div className="flex min-h-6 items-center justify-between">
          <div className="flex items-center gap-2">
            {figure.hideable && (
              <button
                type="button"
                role="checkbox"
                aria-checked={shown}
                aria-label={`${shown ? 'Hide' : 'Show'} ${figure.label}`}
                title={shown ? 'Hide this level' : 'Show this level'}
                onClick={() => onChange({ visible: !shown })}
                className={
                  'flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border transition-colors ' +
                  (shown
                    ? 'border-primary bg-primary text-primary-foreground'
                    : 'border-border-strong bg-transparent text-transparent hover:border-[hsl(var(--muted-foreground))]')
                }
              >
                <svg
                  width={10}
                  height={10}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={3}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="20 6 9 17 4 12" />
                </svg>
              </button>
            )}
            <span
              className={
                'text-xs font-semibold ' +
                (shown ? 'text-[hsl(var(--chart-text))]' : 'text-[hsl(var(--muted-foreground))]')
              }
            >
              {figure.label}
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            {customized && (
              <button
                type="button"
                onClick={onReset}
                title={`Reset ${figure.label.toLowerCase()} to default`}
                aria-label={`Reset ${figure.label} to default`}
                className="flex h-5 w-5 items-center justify-center rounded-sm text-[hsl(var(--muted-foreground))] transition-colors hover:bg-accent hover:text-[hsl(var(--chart-text))]"
              >
                <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor">
                  <path
                    d="M3 12a9 9 0 1 0 3-6.7L3 8"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M3 3v5h5"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            )}
            {/* The Plot type row names the type whenever it can change. */}
            {plotTypeFixed && (
              <span className="text-[10px] uppercase text-[hsl(var(--muted-foreground))]">
                {figure.type}
              </span>
            )}
          </div>
        </div>
      )}

      {!plotTypeFixed && (
        <PlotTypeRow value={plotType} onChange={(t) => onChange({ plotType: t })} />
      )}

      {figure.defaultHistogramColors && !asLine ? (
        <HistogramColorRows figure={figure} override={override} onChange={onChange} />
      ) : hasUpDown ? (
        <div className="grid grid-cols-2 gap-2">
          <ColorRow
            label="Rising"
            color={override.upColor ?? figure.defaultUpColor ?? figure.defaultColor}
            opacity={override.opacity ?? 1}
            onChange={(p) => onChange({ upColor: p.color, opacity: p.opacity })}
          />
          <ColorRow
            label="Falling"
            color={override.downColor ?? figure.defaultDownColor ?? figure.defaultColor}
            opacity={override.opacity ?? 1}
            onChange={(p) => onChange({ downColor: p.color, opacity: p.opacity })}
          />
        </div>
      ) : (
        <ColorRow
          label="Color"
          color={override.color ?? figure.defaultColor}
          opacity={override.opacity ?? 1}
          onChange={(p) => onChange({ color: p.color, opacity: p.opacity })}
        />
      )}

      {/* a histogram's bar is as wide as the line width */}
      {(asLine || plotType === 'histogram') && (
        <LineWidthRow
          value={override.size ?? figure.defaultSize ?? 1}
          onChange={(s) => onChange({ size: s })}
        />
      )}
      {asLine && (
        <LineStyleRow
          value={
            (override.style as 'solid' | 'dashed' | 'dotted') ?? figure.defaultStyle ?? 'solid'
          }
          onChange={(s) => onChange({ style: s })}
        />
      )}
    </div>
  )
}

/** One row per histogram colour, by sign and direction (MACD). A legacy
 *  Rising/Falling override still seeds the two strong swatches, matching what
 *  `buildBarStyle` draws. Opacity is shared across all four. */
const HISTOGRAM_ROWS: Array<{
  key: 'growAboveColor' | 'fallAboveColor' | 'growBelowColor' | 'fallBelowColor'
  label: string
  pick: (c: HistogramColors) => string
  legacy?: 'upColor' | 'downColor'
}> = [
  {
    key: 'growAboveColor',
    label: 'Growing above zero',
    pick: (c) => c.growAbove,
    legacy: 'upColor',
  },
  { key: 'fallAboveColor', label: 'Falling above zero', pick: (c) => c.fallAbove },
  { key: 'growBelowColor', label: 'Growing below zero', pick: (c) => c.growBelow },
  {
    key: 'fallBelowColor',
    label: 'Falling below zero',
    pick: (c) => c.fallBelow,
    legacy: 'downColor',
  },
]

function HistogramColorRows({
  figure,
  override,
  onChange,
}: {
  figure: FigureDeclaration
  override: FigureStyleOverride
  onChange: (patch: Partial<FigureStyleOverride>) => void
}) {
  const defaults = figure.defaultHistogramColors!
  return (
    <>
      {HISTOGRAM_ROWS.map((row) => (
        <ColorRow
          key={row.key}
          label={row.label}
          color={override[row.key] ?? (row.legacy && override[row.legacy]) ?? row.pick(defaults)}
          opacity={override.opacity ?? 1}
          onChange={(p) => onChange({ [row.key]: p.color, opacity: p.opacity })}
        />
      ))}
    </>
  )
}

/* ------------------------------------------------------------------ */
/*  Visibility tab                                                      */
/* ------------------------------------------------------------------ */

/**
 * Per-timeframe visibility. A selectable chip per timeframe, grouped into the
 * three coarse categories (Intraday / Hourly / Daily — daily+ periods share
 * one row); a lit chip means the indicator draws on that timeframe. Deselect
 * the Daily chips for an intraday-only indicator (VWAP); deselect the intraday
 * chips for a daily-only one. No chips deselected ⇒ visible everywhere.
 */
function VisibilityTab({
  rule,
  onToggle,
  onSetAll,
}: {
  rule: TimeframeVisibilityRule
  onToggle: (period: PeriodKey, visible: boolean) => void
  onSetAll: (visible: boolean) => void
}) {
  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[11px] leading-snug text-[hsl(var(--muted-foreground))]">
          Show this indicator only on the timeframes you select below.
        </div>
        <div className="flex shrink-0 gap-1.5">
          <button type="button" onClick={() => onSetAll(true)} className={SECONDARY_BUTTON_CLS}>
            All on
          </button>
          <button type="button" onClick={() => onSetAll(false)} className={SECONDARY_BUTTON_CLS}>
            All off
          </button>
        </div>
      </div>
      {TIMEFRAME_CATEGORY_GROUPS.map((group) => (
        <div key={group.category} className="flex flex-col gap-1.5">
          <div className="text-[10px] font-medium uppercase tracking-wide text-[hsl(var(--muted-foreground))]">
            {group.label}
          </div>
          <div className="flex flex-wrap gap-1.5">
            {group.periods.map((p) => {
              const visible = rule[p.key] !== false
              return (
                <button
                  key={p.key}
                  type="button"
                  role="checkbox"
                  aria-checked={visible}
                  aria-label={p.label}
                  onClick={() => onToggle(p.key, !visible)}
                  className={
                    'h-7 min-w-[42px] rounded-sm border px-2 text-xs font-medium transition-colors ' +
                    (visible
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border-strong bg-transparent text-[hsl(var(--muted-foreground))] hover:bg-accent hover:text-[hsl(var(--chart-text))]')
                  }
                >
                  {p.label}
                </button>
              )
            })}
          </div>
        </div>
      ))}
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Footer                                                              */
/* ------------------------------------------------------------------ */

function Footer({
  isDirty,
  onCancel,
  onApply,
  onReset,
  onSaveAsDefault,
  onApplyToAll,
}: {
  isDirty: boolean
  onCancel: () => void
  onApply: () => void
  onReset: () => void
  onSaveAsDefault?: () => void
  onApplyToAll?: () => void
}) {
  return (
    <div className="flex items-center gap-2 border-t border-border-strong px-3 py-2.5">
      <DefaultsMenu onReset={onReset} onSaveAsDefault={onSaveAsDefault} />
      <div className="flex-1" />
      {onApplyToAll && (
        <button
          onClick={onApplyToAll}
          className={SECONDARY_BUTTON_CLS}
          title="Broadcast to all charts"
        >
          Apply to all
        </button>
      )}
      <button onClick={onCancel} className={SECONDARY_BUTTON_CLS}>
        Cancel
      </button>
      <button onClick={onApply} disabled={!isDirty} className={primaryButtonCls(!isDirty)}>
        OK
      </button>
    </div>
  )
}

/** Bottom-left "Defaults" menu: save-as-default + reset to catalog defaults. */
function DefaultsMenu({
  onReset,
  onSaveAsDefault,
}: {
  onReset: () => void
  onSaveAsDefault?: () => void
}) {
  const [open, setOpen] = useState(false)
  return (
    <div className="relative">
      {open && <div onMouseDown={() => setOpen(false)} className="fixed inset-0 z-[1]" />}
      <button
        onClick={() => setOpen((o) => !o)}
        className={SECONDARY_BUTTON_CLS}
        title="Default settings"
      >
        Defaults ▾
      </button>
      {open && (
        <div className="absolute bottom-[calc(100%+6px)] left-0 z-[2] min-w-[210px] overflow-hidden rounded-md border border-primary/15 bg-popover shadow-lg shadow-black/50">
          {onSaveAsDefault && (
            <MenuItem
              onClick={() => {
                onSaveAsDefault()
                setOpen(false)
              }}
            >
              Save as my default
            </MenuItem>
          )}
          <MenuItem
            onClick={() => {
              onReset()
              setOpen(false)
            }}
          >
            Reset to catalog defaults
          </MenuItem>
        </div>
      )}
    </div>
  )
}

function MenuItem({ onClick, children }: { onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className="block w-full whitespace-nowrap px-3 py-2 text-left text-xs text-[hsl(var(--chart-text))] transition-colors hover:bg-accent"
    >
      {children}
    </button>
  )
}

function UnknownIndicatorNotice({ name }: { name: string }) {
  return (
    <div className="text-xs leading-relaxed text-[hsl(var(--muted-foreground))]">
      <div>No schema for indicator &quot;{name}&quot;.</div>
      <div className="mt-1.5 opacity-70">
        It can still render on the chart, but editing inputs and styles requires a definition in{' '}
        <code>@edge/charts-indicators</code>.
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------ */
/*  Utilities                                                            */
/* ------------------------------------------------------------------ */

function cloneStyles(s: IndicatorStyleOverrides): IndicatorStyleOverrides {
  const out: IndicatorStyleOverrides = {}
  for (const [k, v] of Object.entries(s)) out[k] = { ...v }
  return out
}

function cloneRule(rule: TimeframeVisibilityRule): TimeframeVisibilityRule {
  return { ...rule }
}
